import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { BoundingBox } from "../core/mdl/BoundingBox";
import * as basket from "../eaiws/basket";
import { Log } from "../utils";
import { Helpers } from "./Helpers";
/** @internal */
export class ArticleImport {
    static async Import(pArticleManager, pItems, pProperties, pAddToModel, pOptions) {
        const tImpl = new ArticleImportImpl(pArticleManager);
        return (tImpl.import(pItems, pProperties, pAddToModel, pOptions));
    }
}
/** @internal */
export class ArticleImportImpl {
    constructor(pArticleManager) {
        this.mMngr = pArticleManager;
        this.mImportNoPosArticles = false;
        this.mItemsMap = new Map();
        this.mItemPropsMap = new Map();
        this.mMainArticles = [];
        this.mMainArticleQuantity = new Map();
        this.mSetArticlesToReset = new Set();
        this.mLoadedArticles = [];
        this.mNoPosArticles = [];
    }
    async import(pItems, pProperties, pAddToModel, pOptions) {
        if (pProperties.length === 0 || pItems.length === 0)
            return ([]);
        this.mImportNoPosArticles = pOptions != null && pOptions.importArticlesWithoutPosition === true;
        //build map from basket id to item
        for (const tItem of pItems) {
            this.mItemsMap.set(tItem.itemId, tItem);
        }
        //build map from basket id to properties and collect main articles
        for (const tProps of pProperties) {
            this.mItemPropsMap.set(tProps.itemId, tProps);
            if (tProps.itemId != null && tProps.article != null) {
                const tItem = this.mItemsMap.get(tProps.itemId);
                if (tItem != null && tItem.getMainArticleId() === tItem.itemId) {
                    this.mMainArticles.push(tProps);
                }
            }
        }
        //import articles
        for (const tProps of this.mMainArticles) {
            await this.importArticle(tProps, pAddToModel, pOptions);
        }
        //place articles with no position
        this.placeNoPosArticles();
        //reset quantities of set articles
        const tIdArray = Array.from(this.mSetArticlesToReset);
        for (const tId of tIdArray)
            await this.resetQuantity(tId);
        return (this.mLoadedArticles);
    }
    async importArticle(pProps, pAddToModel, pOptions) {
        const tHasPosition = pProps.composableGeometry != null && pProps.composableGeometry.enabled;
        if (pProps.article == null || (!this.mImportNoPosArticles && !tHasPosition))
            return;
        if (pProps.itemType === "SetArticle")
            return;
        await this.getArticleQuantity(pProps);
        const tItemId = pProps.itemId;
        let tWasUpdated = false;
        const tIsUserArticle = pProps.itemType === "UserArticle";
        if (!tIsUserArticle) {
            tWasUpdated = await this.updateOrMigrateArticle(pProps, pOptions);
        }
        let tArticle = null;
        if (tIsUserArticle) {
            tArticle = await this.mMngr._createUserArticleElement(tItemId, this.mItemsMap, this.mItemPropsMap);
        }
        else {
            tArticle = await this.mMngr._createArticleElement(tItemId, tWasUpdated ? null : this.mItemsMap, //reload items if article was updated
            tWasUpdated ? null : this.mItemPropsMap); //reload properties if article was updated
        }
        if (tArticle != null) {
            this.addArticle(tArticle, pProps, pAddToModel, pOptions);
        }
        else {
            Log.warn("Failed to load article: " +
                pProps.article.manufacturerId + ":" +
                pProps.article.seriesId + ":" +
                pProps.article.baseArticleNumber + " (" + pProps.itemId + ")");
        }
    }
    async updateOrMigrateArticle(pProps, pOptions) {
        if (pOptions == null || pOptions.shouldUpdateCallback == null || pProps.article == null)
            return (false);
        const tItemId = pProps.itemId;
        //get current update state
        try {
            pProps.article.ofmlUpdateState = await Helpers.getOfmlUpdateState(this.mMngr.session, tItemId);
        }
        catch (pError) {
            Log.error("Failed to get ofml update state", pError);
        }
        //update and migrate article
        if ((pProps.article.ofmlUpdateState === "Updatable" ||
            pProps.article.ofmlUpdateState === "Migratable") &&
            pOptions.shouldUpdateCallback(pProps)) {
            try {
                await Helpers.updateOfmlArticle(this.mMngr.session, tItemId, true);
                return (true);
            }
            catch (pError) {
                Log.error("Failed to update article", pError);
            }
        }
        return (false);
    }
    async getArticleQuantity(pProps) {
        if (pProps.article == null || pProps.article.quantity == null)
            return;
        const tItemId = pProps.itemId;
        let tQuantity = pProps.article.quantity;
        //get set article quantity
        if (pProps.setArticleId != null) {
            const tSet = this.mItemPropsMap.get(pProps.setArticleId);
            if (tSet != null && tSet.article != null && tSet.article.quantity != null) {
                tQuantity *= tSet.article.quantity;
                this.mSetArticlesToReset.add(tSet.itemId);
            }
        }
        this.mMainArticleQuantity.set(tItemId, tQuantity);
        //reset quantity to 1
        if (pProps.article.quantity > 1) {
            pProps.article.quantity = 1;
            await this.resetQuantity(tItemId);
        }
    }
    resetQuantity(pItemId) {
        const tNewProps = new basket.ItemProperties();
        tNewProps.article = new basket.ArticleProperties();
        tNewProps.article.quantity = 1;
        return this.mMngr.session.basket.setItemProperties(pItemId, tNewProps);
    }
    addArticle(pArticle, pProps, pAddToModel, pOptions) {
        let tQuantity = this.mMainArticleQuantity.get(pProps.itemId);
        if (tQuantity == null)
            tQuantity = 1;
        if (pProps.composableGeometry != null && pProps.composableGeometry.enabled) {
            pArticle._loadEaiwsTransform(pProps.composableGeometry);
            this.mLoadedArticles.push(pArticle);
        }
        else {
            this.mNoPosArticles.push(pArticle);
        }
        if (pAddToModel)
            this.mMngr.app.model.addElement(pArticle);
        //create no position clones
        if (this.mImportNoPosArticles) {
            for (let i = 1; i < tQuantity; ++i) {
                const tClone = pArticle.clone();
                if (tClone != null) {
                    this.mNoPosArticles.push(tClone);
                    if (pAddToModel)
                        this.mMngr.app.model.addElement(tClone);
                }
            }
        }
        this.mMngr.app.viewer.requestRenderFrame();
    }
    placeNoPosArticles() {
        if (this.mNoPosArticles.length === 0)
            return;
        //get grid size
        let tSizeX = 0;
        let tSizeZ = 0;
        for (const tArticle of this.mNoPosArticles) {
            tArticle.setTransform(Matrix.Identity()); // reset tranform, maybe it was cloned
            const tSize = tArticle.boundingBoxVisible.getSize();
            tSizeX = Math.max(tSizeX, tSize.x);
            tSizeZ = Math.max(tSizeZ, tSize.z);
        }
        tSizeX += 0.1; //10 cm gap between articles
        tSizeZ += 0.1; //10 cm gap between articles
        //get grid offset
        const tCountX = Math.ceil(Math.sqrt(this.mNoPosArticles.length));
        const tCountZ = Math.ceil(this.mNoPosArticles.length / tCountX);
        let tOffsetX = (tSizeX * tCountX) * -0.5;
        let tOffsetZ = (tSizeZ * tCountZ) * -0.5;
        if (this.mLoadedArticles.length > 0) {
            const tBounds = BoundingBox.FromElements(this.mLoadedArticles, undefined, true);
            tOffsetX += tBounds.max.x + (tSizeX * tCountX) * 0.5 + tSizeX;
            tOffsetZ -= tBounds.min.z + (tSizeZ * tCountZ) * 0.5;
        }
        //place articles
        for (let z = 0; z < tCountZ; ++z) {
            for (let x = 0; x < tCountX; ++x) {
                const tIndex = x + tCountX * z;
                if (tIndex >= this.mNoPosArticles.length)
                    break;
                const tArticle = this.mNoPosArticles[tIndex];
                const tSize = tArticle.boundingBoxVisible.getSize();
                const tCenterOffsetX = -((tSize.x * 0.5) + tArticle.boundingBoxVisible.min.x);
                const tCenterOffsetZ = (tSize.z * 0.5) + tArticle.boundingBoxVisible.min.z;
                tArticle.node.position.x = tSizeX * x + tOffsetX + tCenterOffsetX;
                tArticle.node.position.z = -(tSizeZ * z + tOffsetZ + tCenterOffsetZ);
                tArticle.updateTransformation();
            }
        }
        this.mLoadedArticles.push(...this.mNoPosArticles);
        this.mMngr.app.viewer.requestRenderFrame();
    }
}
